import { useMemo } from "react";

import { createQueryKeys } from "@lukemorales/query-key-factory";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { camelCaseKeys } from "src/utility/utilityFunctions";

import { useCurrentOrderSet } from "@features/orders/orderSet/data/CurrentOrderSetContext";
import { BudgetSpend } from "@models/Budget";
import client, { RequestParams } from "@services/api";
import { QueryOptions } from "@utils/reactQuery";
import useDebouncedValue from "@utils/useDebouncedValue";

export const budgetSpendKeyFactory = createQueryKeys("budget-spend", {
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<any[]>("budget-spend", { params, deserializeOutput: false })
        .then(
          (data) =>
            data.map((spend) => ({
              ...camelCaseKeys(spend),
              id: String(spend.id),
            })) as BudgetSpend[]
        ),
  }),
});

export const useBudgetSpendQuery = (
  params: RequestParams,
  options?: QueryOptions<BudgetSpend[]>
) =>
  useQuery({
    ...budgetSpendKeyFactory.list(params),
    ...options,
  });

const DEBOUNCE_DELAY = 5000;
// A debounced spend query for coupon budgets on the current order set.
export const useCouponBudgetSpendQuery = () => {
  const { orderSetId, orders } = useCurrentOrderSet();

  const { budgetIds, totalCouponQty } = useMemo(() => {
    const budgetIds = new Set<string>();
    let totalCouponQty = 0;
    for (const order of orders) {
      for (const ov of order.orderVariants) {
        if (ov.variant.item.isCoupon) {
          ov.budgets.forEach((b) => budgetIds.add(b.id));
          totalCouponQty += ov.qty;
        }
      }
    }
    return { budgetIds: Array.from(budgetIds), totalCouponQty };
  }, [orders]);

  const debouncedCouponQty = useDebouncedValue(totalCouponQty, DEBOUNCE_DELAY);

  const { data, isFetching } = useBudgetSpendQuery(
    {
      filter: {
        budgetIds,
        orderSetId: orderSetId,
        _refetch: debouncedCouponQty,
      },
    },
    {
      staleTime: 60_000,
      enabled: !!budgetIds.length,
      placeholderData: keepPreviousData,
    }
  );

  const budgetSpends = useMemo(() => {
    const map = new Map<string, BudgetSpend>();
    data?.forEach((spend) => {
      map.set(spend.id, spend);
    });
    return map;
  }, [data]);

  const getCouponRedemptionAndFees = (budgetId: string): number | null => {
    const couponSpend = budgetSpends.get(budgetId);
    if (!couponSpend) return null;

    return (
      +couponSpend.couponTotalRedemptions +
      +couponSpend.couponTotalFixedFees +
      +couponSpend.couponTotalProcessingFees +
      +couponSpend.couponTotalCarryingFees
    );
  };

  return {
    hasCouponBudgetSpend: budgetSpends.size > 0,
    /**
     * Budgets this order set reaches through coupon items, known from the first
     * render. `hasCouponBudgetSpend` above is derived from loaded data, so it
     * cannot tell "no coupon budgets" from "their spend hasn't arrived".
     */
    couponBudgetIds: budgetIds,
    isFetching,
    isCouponSpendsUpToDate:
      totalCouponQty === debouncedCouponQty && !isFetching,
    getCouponRedemptionAndFees,
  };
};
